/**
 * Whether a create failure is a unique (raceId, filename) conflict (409).
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isPhotoFilenameConflictError(error) {
  if (!error || typeof error !== "object") {
    return false;
  }
  const status = error.response?.status ?? error.status;
  if (status === 409) {
    return true;
  }
  return error.code === "photo/filenameConflict";
}

/**
 * Uploads raw image bytes to a GCS emulator (POST media) or signed PUT URL.
 * Does not send Authorization.
 *
 * @param {object} params
 * @param {string} params.uploadUrl
 * @param {string} params.contentType
 * @param {Blob|Uint8Array|ArrayBuffer} params.body
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<void>}
 */
export async function uploadPhotoBytes({ uploadUrl, contentType, body, signal }) {
  const method = String(uploadUrl).includes("uploadType=media") ? "POST" : "PUT";
  const response = await fetch(uploadUrl, {
    method,
    headers: { "Content-Type": contentType },
    body,
    signal,
  });
  if (!response.ok) {
    throw new Error(`Photo byte upload failed with status ${response.status}`);
  }
}

/**
 * Whether the GUI should POST /photo/uploaded after GCS bytes.
 * Off by default: cloud uses GCS object.finalize Eventarc (see deploy-gcpfuncs-dev.yml).
 * Enable locally with `VITE_PHOTO_CONFIRM_UPLOAD=true` (fake-gcs has no notifications).
 *
 * @param {{ VITE_PHOTO_CONFIRM_UPLOAD?: string }} [env]
 * @returns {boolean}
 */
export function isPhotoConfirmUploadEnabled(env = import.meta.env) {
  return env?.VITE_PHOTO_CONFIRM_UPLOAD === "true";
}

/**
 * Sequential create → GCS bytes → optional uploaded. Continues after per-item errors.
 * Filename conflicts are skipped. Aborts before starting the next item when `signal` is aborted.
 *
 * @param {object} params
 * @param {string} params.raceId
 * @param {Array<{ localId: string, filename: string, contentType: string, tags?: string[], getBytes: Function, photoId?: string }>} params.items
 * @param {Function} params.createPhoto
 * @param {Function} params.uploadBytes
 * @param {Function} [params.confirmUploaded] Called only when confirm-upload is enabled.
 * @param {boolean} [params.confirmUpload] Override for `isPhotoConfirmUploadEnabled()`.
 * @param {Function} [params.onItemStart]
 * @param {Function} [params.onItemSuccess]
 * @param {Function} [params.onItemSkip]
 * @param {Function} [params.onItemError]
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<void>}
 */
export async function runPhotoUploadQueue({
  raceId,
  items,
  createPhoto,
  uploadBytes,
  confirmUploaded,
  confirmUpload = isPhotoConfirmUploadEnabled(),
  onItemStart,
  onItemSuccess,
  onItemSkip,
  onItemError,
  signal,
}) {
  for (const item of items) {
    if (signal?.aborted) {
      return;
    }

    onItemStart?.(item);

    try {
      const created = await createPhoto({
        raceId,
        filename: item.filename,
        contentType: item.contentType,
        tags: item.tags,
      });
      item.photoId = created?._id != null ? String(created._id) : undefined;

      const body = await item.getBytes();
      await uploadBytes({
        uploadUrl: created.uploadUrl,
        contentType: item.contentType,
        body,
        signal,
      });
      if (confirmUpload) {
        await confirmUploaded({ photoId: created._id });
      }
      onItemSuccess?.(item, created);
    } catch (error) {
      if (isPhotoFilenameConflictError(error)) {
        onItemSkip?.(item, error);
        continue;
      }
      onItemError?.(item, error);
    }
  }
}
